import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toApiError } from '../../api/errors';
import type { Id } from '../../api/types';
import { EmptyState } from '../../components/ui/EmptyState';
import { ErrorNotice, StaleBanner } from '../../components/ui/Notice';
import { SkeletonBar } from '../../components/ui/Skeleton';
import { useResource } from '../../hooks/useResource';
import { useProjects } from '../../projects/ProjectsProvider';
import { PROJECT_PARAM } from '../../components/layout/nav-links';
import { MissedList } from './MissedList';
import { buildRows, todayIso } from './occurrence-model';
import { OccurrenceList } from './OccurrenceList';
import {
  createRecurrence,
  fetchMissedOccurrences,
  fetchOccurrences,
  fetchRecurrences,
  generateForProject,
  materialiseOccurrence,
  setRecurrenceActive,
  type CreateRecurrenceBody,
  type MissedOccurrence,
  type Occurrence,
  type Recurrence,
} from './recurrence-api';
import { TemplateForm } from './TemplateForm';
import { TemplateList } from './TemplateList';

/**
 * RECURRING WORK — the missed-duty query, and the templates behind it.
 *
 * ⭐⭐ MISSED IS THE DEFAULT TAB, and that is a decision rather than an ordering. The templates are
 * the configuration; the missed list is the ANSWER. db/002 exists so that "was the weekly update done
 * on 12 Aug?" is a query — landing on the template list would put one more step in front of the only
 * thing that has ever gone wrong here.
 *
 * ⭐ AND THE MISSED TAB IS UNSCOPED. `GET /recurrences/missed` takes an OPTIONAL `projectId` and the
 * API's own comment explains why the optionality matters: a per-project-only version puts the burden
 * back on somebody remembering to check each project, which is the failure the design replaced. So
 * the default is every project at once, and the shell's `?project=` NARROWS it rather than being
 * required.
 *
 * ⛔ The templates tab genuinely needs a project — `GET /projects/{id}/recurrences` is per project and
 * there is no global template list. That is stated on screen rather than worked around.
 *
 * ⚠️ Renders no app chrome. Must be inside `ProjectsProvider`.
 */
export function RecurrencePage() {
  const [params, setParams] = useSearchParams();
  const projectId = params.get(PROJECT_PARAM);
  const tab = params.get('tab') === 'templates' ? 'templates' : 'missed';
  const projects = useProjects();

  const setTab = useCallback(
    (next: 'missed' | 'templates') => {
      const search = new URLSearchParams(params);
      if (next === 'missed') search.delete('tab');
      else search.set('tab', next);
      setParams(search, { replace: true });
    },
    [params, setParams],
  );

  const [scopeToProject, setScopeToProject] = useState(false);
  const missedScope = scopeToProject ? projectId : null;

  const missed = useResource<MissedOccurrence[]>(
    (signal) => fetchMissedOccurrences({ projectId: missedScope }, signal),
    [missedScope],
  );

  const templates = useResource<Recurrence[]>(
    (signal) => fetchRecurrences(projectId as Id, signal),
    [projectId],
    { enabled: Boolean(projectId) },
  );

  const [openTemplate, setOpenTemplate] = useState<Id | null>(null);
  const occurrences = useResource<Occurrence[]>(
    (signal) => fetchOccurrences(openTemplate as Id, signal),
    [openTemplate],
    { enabled: Boolean(openTemplate) },
  );

  /** ⭐ Captured once per render pass — reading the clock per comparison lets one list classify its
   *  first row against one day and its last against the next. */
  const today = useMemo(() => todayIso(), []);
  const occurrenceRows = useMemo(
    () => buildRows(occurrences.data ?? [], templates.data ?? [], today),
    [occurrences.data, templates.data, today],
  );

  const [busyId, setBusyId] = useState<Id | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [createError, setCreateError] = useState<string | null>(null);

  const record = useCallback((key: string, err: unknown, path: string) => {
    const apiError = toApiError(err, path);
    setErrors((previous) => ({
      ...previous,
      [key]: `${apiError.message}${apiError.reason ? ` — ${apiError.reason}` : ''}`,
    }));
  }, []);

  const run = useCallback(
    async (key: Id, path: string, work: () => Promise<void>, after: () => void) => {
      setBusyId(key);
      setErrors((previous) => {
        const next = { ...previous };
        delete next[String(key)];
        return next;
      });
      try {
        await work();
        after();
      } catch (err) {
        // 🔴 The API's refusal, verbatim. Membership, an already-promoted item, a template with
        // history — those rules live there, and re-implementing them here to grey a button out would
        // be a second copy that drifts.
        record(String(key), err, path);
      } finally {
        setBusyId(null);
      }
    },
    [record],
  );

  const missedTotal = missed.data?.length ?? 0;

  return (
    <div className="flex h-full min-h-0 flex-col bg-[color:var(--color-surface)]">
      <header className="border-b border-[color:var(--color-line)]">
        <div className="flex flex-wrap items-center gap-3 px-3 py-2 sm:px-4">
          <h1 className="text-base font-semibold">Recurring work</h1>
          {/*
            ⭐⭐ THE MISSED COUNT IS IN THE HEADER, on both tabs, and ONLY when we have an answer.
            `missed.data` being null means the request failed or is in flight — printing 0 there
            would be an all-clear from a screen that has not looked.
          */}
          {missed.data && missedTotal > 0 ? (
            <span className="rounded-full bg-red-600 px-2 py-0.5 text-xs font-bold text-white">
              {missedTotal} missed
            </span>
          ) : null}
          <span className="flex-1" />
          <nav className="flex gap-1" aria-label="Recurrence views">
            {(
              [
                ['missed', 'Missed'],
                ['templates', 'Templates'],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setTab(id)}
                aria-current={tab === id}
                className={`h-8 rounded-full px-3 text-xs font-semibold ${
                  tab === id
                    ? 'bg-[color:var(--color-ink)] text-[color:var(--color-surface)]'
                    : 'bg-[color:var(--color-line)] text-[color:var(--color-ink-muted)]'
                }`}
              >
                {label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="space-y-3 p-3 sm:p-4">
          {projects.error ? <ErrorNotice error={projects.error} onRetry={projects.reload} /> : null}

          {tab === 'missed' ? (
            <>
              {missed.error && !missed.isStale ? (
                <ErrorNotice error={missed.error} onRetry={missed.reload} />
              ) : null}
              {missed.isStale && missed.error ? (
                <StaleBanner error={missed.error} loadedAt={missed.loadedAt} onRetry={missed.reload} />
              ) : null}
            </>
          ) : (
            <>
              {templates.error && !templates.isStale ? (
                <ErrorNotice error={templates.error} onRetry={templates.reload} />
              ) : null}
              {templates.isStale && templates.error ? (
                <StaleBanner
                  error={templates.error}
                  loadedAt={templates.loadedAt}
                  onRetry={templates.reload}
                />
              ) : null}
            </>
          )}
        </div>

        {tab === 'missed' ? (
          <>
            <div className="px-3 pb-2 sm:px-4">
              <label className="flex items-center gap-2 text-xs text-[color:var(--color-ink-muted)]">
                <input
                  type="checkbox"
                  checked={scopeToProject}
                  disabled={!projectId}
                  onChange={(event) => setScopeToProject(event.target.checked)}
                  className="size-4"
                />
                {/*
                  ⛔ NARROWING IS OPT-IN AND OFF BY DEFAULT. Scoping by default would mean somebody has
                  to remember to check each project in turn — the exact "a human notices" failure the
                  one-row-per-period design replaced.
                */}
                Only this project
                {projectId ? '' : ' — choose a project in the rail to enable'}
              </label>
            </div>

            {missed.isLoading ? (
              <div role="status" aria-label="Loading missed periods" className="px-3 sm:px-4">
                <SkeletonBar className="h-24 w-full" />
              </div>
            ) : null}

            {/*
              🔴 RENDERED ONLY WHEN WE HAVE AN ANSWER. `MissedList`'s all-clear ("No missed periods")
              is a claim about the data, so it must be structurally unreachable when the request
              failed — enforced here, by where the component is mounted, rather than by a flag inside
              it that could be got wrong.
            */}
            {missed.data ? (
              <MissedList
                rows={missed.data}
                projects={projects.data ?? []}
                busyId={busyId}
                errors={errors}
                onMaterialise={(row) =>
                  void run(row.id, `/occurrences/${row.id}/ticket`, () => materialiseOccurrence(row.id), () => {
                    missed.reload();
                    if (openTemplate) occurrences.reload();
                    templates.reload();
                  })
                }
              />
            ) : null}
          </>
        ) : null}

        {tab === 'templates' ? (
          <>
            {!projectId ? (
              <EmptyState
                title="Choose a project"
                detail="Recurring templates belong to a project — GET /projects/{id}/recurrences is per project and there is no global list. The MISSED tab, however, spans every project."
                action={
                  <button
                    type="button"
                    onClick={() => setTab('missed')}
                    className="h-9 rounded border border-[color:var(--color-line)] px-3 text-sm font-semibold"
                  >
                    Show missed periods across all projects
                  </button>
                }
              />
            ) : null}

            {projectId && templates.isLoading ? (
              <div role="status" aria-label="Loading templates" className="px-3 sm:px-4">
                <SkeletonBar className="h-24 w-full" />
              </div>
            ) : null}

            {/* ⛔ `EmptyState`, not a Notice — "this project has none" is a claim about DATA, and it
                is reachable only because `templates.data` proves the request answered. */}
            {projectId && templates.data && templates.data.length === 0 ? (
              <EmptyState
                title="This project has no recurring duties"
                detail="The request succeeded and returned none — an empty list, not a failure. A template generates one ticket per period; without one, nothing is expected and nothing can be missed."
              />
            ) : null}

            {projectId && templates.data ? (
              <>
                <div className="flex flex-wrap items-center gap-2 px-3 pb-2 sm:px-4">
                  {/*
                    ⭐ The generator, as a button. Idempotent — running it twice with the same date
                    writes nothing the second time — so it is safe to press when a duty looks stale.
                    ⛔ It does NOT back-fill tickets for missed periods; it writes the EXPECTED rows.
                  */}
                  <button
                    type="button"
                    disabled={busyId === projectId}
                    onClick={() =>
                      void run(
                        projectId,
                        `/projects/${projectId}/recurrences/generate`,
                        () => generateForProject(projectId),
                        () => {
                          templates.reload();
                          missed.reload();
                          if (openTemplate) occurrences.reload();
                        },
                      )
                    }
                    className="h-8 rounded border border-[color:var(--color-line)] px-3 text-xs font-semibold disabled:opacity-50"
                  >
                    {busyId === projectId ? 'Generating…' : 'Generate expected periods'}
                  </button>
                  <span className="text-xs text-[color:var(--color-ink-muted)]">
                    Writes the periods that are due. ⛔ It does not create tickets for periods already
                    missed — that would erase the evidence they were skipped.
                  </span>
                  {errors[String(projectId)] ? (
                    <p role="alert" className="w-full text-xs font-semibold text-red-700 dark:text-red-300">
                      {errors[String(projectId)]}
                    </p>
                  ) : null}
                </div>

                <TemplateList
                  templates={templates.data}
                  openTemplate={openTemplate}
                  busyId={busyId}
                  errors={errors}
                  onToggleOpen={(template) =>
                    setOpenTemplate((current) =>
                      String(current) === String(template.id) ? null : template.id,
                    )
                  }
                  onTogglePaused={(template) =>
                    void run(
                      template.id,
                      `/recurrences/${template.id}`,
                      () => setRecurrenceActive(template.id, !template.active),
                      () => {
                        templates.reload();
                        missed.reload();
                      },
                    )
                  }
                  renderOccurrences={(template) =>
                    String(openTemplate) === String(template.id) ? (
                      <div className="mt-3 border-t border-[color:var(--color-line)] pt-3">
                        {occurrences.error && !occurrences.isStale ? (
                          <ErrorNotice error={occurrences.error} onRetry={occurrences.reload} />
                        ) : null}
                        {occurrences.isLoading ? <SkeletonBar className="h-16 w-full" /> : null}
                        {/* ⛔ Same rule as the missed tab: the all-clear line inside this list is only
                            reachable when the request actually answered. */}
                        {occurrences.data ? (
                          <OccurrenceList rows={occurrenceRows} today={today} />
                        ) : null}
                      </div>
                    ) : null
                  }
                />

                <TemplateForm
                  busy={busyId === 'new'}
                  error={createError}
                  onCreate={(body: CreateRecurrenceBody) => {
                    setCreateError(null);
                    void (async () => {
                      setBusyId('new');
                      try {
                        await createRecurrence(projectId, body);
                        templates.reload();
                      } catch (err) {
                        const apiError = toApiError(err, `/projects/${projectId}/recurrences`);
                        setCreateError(
                          `${apiError.message}${apiError.reason ? ` — ${apiError.reason}` : ''}`,
                        );
                      } finally {
                        setBusyId(null);
                      }
                    })();
                  }}
                />
              </>
            ) : null}
          </>
        ) : null}

        {tab === 'missed' && missed.data ? (
          <p className="px-3 pb-6 text-xs text-[color:var(--color-ink-muted)] sm:px-4">
            {missedScope ? 'This project only' : 'Every project'} · paused templates are INCLUDED —
            a duty paused mid-week still has real periods that were missed before the pause, and
            hiding them would let "turn it off" launder a gap.
          </p>
        ) : null}
      </div>
    </div>
  );
}

export default RecurrencePage;
